import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@design-system';
import { ShopInsuranceNotice } from '@/features/shop-insurance';
import { AiUnderwritingCtaAlert } from '@/shared/components/ai-underwriting-cta-alert';
import { AdvisorCustomDisclosures } from '@/features/client-onboarding/components/advisor-custom-disclosures';

import { getStepIndex } from '@/features/guided-financial-planning/config';
import { guidedPlanningQuoteKeys } from '@/features/guided-financial-planning/api';

import {
  useSession,
  useIsSessionComplete,
  useSessionMutations,
  useGuidedPlanningFlatStepConfigs,
} from '@/features/guided-financial-planning/context';
import {
  useAddPolicyFromQuote,
  useGuidedPlanningQuotes,
  useCoverageTypeToggle,
  getQuoteId,
  type GuidedPlanningQuote,
} from '@/features/guided-financial-planning/hooks';
import { useQuotesByPaymentTab } from '@/features/guided-financial-planning/hooks/use-quotes-by-payment-tab';
import { usePerTabPremiumState } from '@/features/guided-financial-planning/hooks/use-per-tab-premium-state';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { GuidedPlanningFooter } from '@/features/guided-financial-planning/components/guided-planning-footer';
import { LicensedAgentCta } from '@/features/guided-financial-planning/components/licensed-agent-cta';
import { QuotesTabView } from '@/features/guided-financial-planning/components/quotes-tab-view';
import { QuotesLoadingState, QuotesErrorState, QuotesEmptyState } from './quotes-ui-states';
import {
  AddPartnerDialog,
  LinkExistingPartnerDialog,
  PartnerRequiredDialog,
} from '@/features/add-partner';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSelector } from 'react-redux';
import { selectClient, selectSession, selectFeatureFlags } from '@client/model/selectors';
import { SessionType } from '@shared';

export function QuotesSelectionStep() {
  const { flags } = useFeatureFlags();
  const queryClient = useQueryClient();
  const { nextStep, goToStep } = useWizard();
  const { session } = useSession();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();
  const { updateStepData, isSaving } = useSessionMutations();
  const clientId = session?.clientId ?? '';
  const isSessionComplete = useIsSessionComplete();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: quotes,
    isLoading,
    isError,
    noQuotesReason,
    isRefetching,
    isFallback,
  } = useGuidedPlanningQuotes(clientId, session);

  const {
    selectedCoverageType,
    setSelectedCoverageType,
    handleCoverageTypeChange,
    isMarriedOrPartnered,
    showPartnerRequiredDialog,
    setShowPartnerRequiredDialog,
    availableTabs,
    filterQuotesByCoverageType,
  } = useCoverageTypeToggle();

  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const [showAddPartnerDialog, setShowAddPartnerDialog] = useState(false);
  const [showLinkExistingDialog, setShowLinkExistingDialog] = useState(false);

  const coverageFilteredQuotes = isD2C
    ? quotes?.filter(q => {
        if ('maritalClass' in q) return q.maritalClass === 'Single';
        const pq = q as PolicyQuote;
        return pq.policyType === 'Single Life' || pq.policyType === 'Standard';
      })
    : filterQuotesByCoverageType(quotes);

  // Group quotes by payment frequency — tabs + per-tab inline sliders
  const quotesDisplay = useQuotesByPaymentTab({
    quotes: coverageFilteredQuotes,
  });

  const perTabPremium = usePerTabPremiumState({
    tabs: quotesDisplay.tabs,
    client,
  });

  const { addQuote, removeQuote, isQuoteAdded, selectedQuote } = useAddPolicyFromQuote();
  const hasSelectedQuote = selectedQuote !== null;

  const handleSelectQuote = (quote: GuidedPlanningQuote) => {
    addQuote(quote);
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('quoteId', getQuoteId(quote));
        return newParams;
      },
      { replace: true },
    );
    nextStep();
  };

  const handleRemoveQuote = (quote: GuidedPlanningQuote) => {
    removeQuote(quote);
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('quoteId');
        return newParams;
      },
      { replace: true },
    );
  };

  const handleBack = useCallback(() => {
    goToStep(getStepIndex('preferences-benefit-structure', flatStepConfigs));
  }, [goToStep, flatStepConfigs]);

  const handleSkipPolicy = useCallback(() => {
    void updateStepData({ selectedPlanType: null }).then(() => {
      goToStep(getStepIndex('plan-comparison', flatStepConfigs));
    });
  }, [goToStep, flatStepConfigs, updateStepData]);

  const hasQuotes = quotes && quotes.length > 0;
  const isAgeIneligible = noQuotesReason === 'age_out_of_range';

  const customFooter = (
    <div>
      <GuidedPlanningFooter
        isNextDisabled={!hasSelectedQuote && !isAgeIneligible}
        nextButtonLabel={isAgeIneligible ? 'Continue with self-funding plan' : undefined}
        backButtonLabel="Adjust Selection"
        onBack={handleBack}
        onNext={isAgeIneligible ? handleSkipPolicy : undefined}
      />
      {!hasSelectedQuote && !isSessionComplete && !isAgeIneligible && (
        <div className="flex justify-center pb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkipPolicy}
            className="text-muted-foreground hover:text-foreground"
          >
            Skip — I don't want to model a policy
          </Button>
        </div>
      )}
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
        <ShopInsuranceNotice />
      </div>
      <AdvisorCustomDisclosures className="mt-2" />
    </div>
  );

  const title = isFallback ? "We couldn't find your preferred options" : 'Your Insurance Options';
  const subtitle = isFallback
    ? 'Here are the other options available. Pick one to see how it fits your plan.'
    : "We've organized options by how you'd pay. Pick one to see how it fits your plan.";

  return (
    <StepLayout
      title={title}
      subtitle={subtitle}
      footer={customFooter}
    >
      {isLoading && <QuotesLoadingState />}

      {isError && <QuotesErrorState />}

      {!isLoading && !isError && !hasQuotes && (
        <QuotesEmptyState
          reason={noQuotesReason}
          filterPanel={null}
        />
      )}

      {!isLoading && !isError && hasQuotes && (
        <div className="space-y-4">
          <AiUnderwritingCtaAlert />

          <QuotesTabView
            tabs={quotesDisplay.tabs}
            defaultTab={quotesDisplay.defaultTab}
            isSessionComplete={isSessionComplete}
            hasSelectedQuote={hasSelectedQuote}
            isQuoteAdded={isQuoteAdded}
            onSelectQuote={handleSelectQuote}
            onRemoveQuote={handleRemoveQuote}
            perTabPremium={perTabPremium}
            showHelpAlert={isD2C}
            coverageOptions={!isD2C && isMarriedOrPartnered ? availableTabs : undefined}
            selectedCoverageType={selectedCoverageType}
            onCoverageTypeChange={!isD2C ? handleCoverageTypeChange : undefined}
          />

          {isD2C && <LicensedAgentCta className="pt-2" />}
        </div>
      )}

      {!isD2C && (
        <>
          <PartnerRequiredDialog
            open={showPartnerRequiredDialog}
            onOpenChange={setShowPartnerRequiredDialog}
            onTakeMeToIt={() => {
              setShowPartnerRequiredDialog(false);
              setShowAddPartnerDialog(true);
            }}
            onLinkExisting={
              isAdvisor
                ? () => {
                    setShowPartnerRequiredDialog(false);
                    setShowLinkExistingDialog(true);
                  }
                : undefined
            }
          />

          {isAdvisor && (
            <LinkExistingPartnerDialog
              open={showLinkExistingDialog}
              client={client}
              onClose={() => setShowLinkExistingDialog(false)}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: guidedPlanningQuoteKeys.all });
                setSelectedCoverageType('joint');
              }}
            />
          )}

          <AddPartnerDialog
            open={showAddPartnerDialog}
            onOpenChange={setShowAddPartnerDialog}
            clientId={clientId}
            onSubmitSuccess={() => {
              setShowAddPartnerDialog(false);
              queryClient.invalidateQueries({ queryKey: guidedPlanningQuoteKeys.all });
              setSelectedCoverageType('joint');
            }}
          />
        </>
      )}
    </StepLayout>
  );
}
